import React from 'react';
import { Scale, Mail, Phone } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Scale className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Terms and Conditions</h1>
            <p className="text-gray-300">Last Updated: March 30, 2025</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="lead">
              Welcome to Stinger Builds. We're here to help you build smarter with precision software and real solutions. These Terms and Conditions ("Terms") outline how we do business and what you can expect when working with us or using our products. By accessing our website, purchasing a license, or working with us on a project, you agree to these Terms.
            </p>

            <h2>1. Scope of Services</h2>
            <p>
              Stinger Builds LLC ("we", "us", or "our") provides custom software development, consulting, and ongoing technology support. Each engagement is defined by a Statement of Work, Service Agreement, or Subscription Plan.
            </p>

            <h2>2. Engagement & Payment</h2>
            <ul>
              <li>Projects and product licenses begin upon acceptance and initial payment.</li>
              <li>Invoices are due within 15 days unless otherwise agreed in writing.</li>
              <li>Late payments may incur a 2% monthly fee.</li>
              <li>We may pause or terminate service due to non-payment.</li>
            </ul>

            <h2>3. Intellectual Property</h2>
            <ul>
              <li>Final deliverables become your property upon full payment.</li>
              <li>We retain ownership of proprietary frameworks, libraries, and base code.</li>
              <li>You may not resell or redistribute our licensed software without written consent.</li>
            </ul>

            <h2>4. Confidentiality</h2>
            <p>
              We keep your information confidential and expect the same in return. All data, credentials, and internal materials are handled with care and respect.
            </p>

            <h2>5. Client Responsibilities</h2>
            <ul>
              <li>Provide timely communication, approvals, and access to required systems.</li>
              <li>Ensure content and assets you provide are licensed or owned by you.</li>
              <li>Assign a primary contact for all project or support communications.</li>
            </ul>

            <h2>6. Warranties & Limitations</h2>
            <ul>
              <li>We warranty our work for 30 days after delivery for bugs or functionality issues.</li>
              <li>We are not responsible for third-party failures (hosting, APIs, etc.) or unauthorized code changes.</li>
              <li>Liability is limited to the amount paid for our services.</li>
            </ul>

            <h2>7. Termination</h2>
            <p>
              You or we may terminate an agreement with written notice. Any work completed up to that point will be invoiced accordingly. Recurring products may be canceled with 30 days' notice.
            </p>

            <h2>8. Dispute Resolution</h2>
            <p>
              Disputes are handled under Arizona law. We'll always try to resolve things professionally and fairly.
            </p>

            <h2>Product-Specific Terms</h2>

            <h3>9. SureTrack CRM</h3>
            <ul>
              <li>License: Monthly or annual subscriptions cover software access, basic support, and updates.</li>
              <li>Support: Includes email-based support and bug fixes. Customizations or advanced integrations are billed separately.</li>
              <li>Data: You own your data. Export options are available. We do not share or resell customer data.</li>
              <li>Uptime: SureTrack is cloud-hosted with 99.9% uptime. Maintenance windows are announced in advance.</li>
            </ul>

            <h3>10. Device Update</h3>
            <ul>
              <li>Monitoring Scope: Includes ICMP checks, heartbeat status, and simple alert logic.</li>
              <li>Data Retention: Standard retention is 30 days; longer periods may be requested.</li>
              <li>Alerting: Email alerts are included; SMS or integrations may require additional configuration.</li>
              <li>Customization: Tailored monitoring or dashboards may incur additional costs.</li>
            </ul>

            <h3>11. Drive the Demand</h3>
            <ul>
              <li>Campaign Materials: Provided as-is and may be modified by the user.</li>
              <li>Usage Rights: Templates and content may be used for internal business purposes. Redistribution is not permitted.</li>
              <li>Support: Includes help with deployment and use of provided materials. Strategy consulting may be offered separately.</li>
            </ul>

            <h3>12. Standup Feed</h3>
            <ul>
              <li>Access: Licensed per user or per team. Tiered pricing is available.</li>
              <li>Integrations: Available for Microsoft Teams, Slack, and email.</li>
              <li>Use Case: Designed for async team reporting. It is not a replacement for full project management tools.</li>
              <li>Data: Stored securely, with export available. We do not analyze or share standup content.</li>
            </ul>

            <h3>13. ClientCliq</h3>
            <ul>
              <li>Functionality: Includes threaded discussions, video calling, and file sharing.</li>
              <li>Compliance: Encrypted messaging and secure access are standard. Not designed for HIPAA or regulated environments without additional configuration.</li>
              <li>Storage: Standard plans include 30-day message history. Extended storage may be available.</li>
              <li>Abuse Policy: We reserve the right to suspend access for users engaging in spam, harassment, or abuse.</li>
            </ul>

            <h2>14. Changes to These Terms</h2>
            <p>
              We may update these Terms periodically. Continued use of our services implies acceptance of the updated Terms.
            </p>

            <h2>Questions?</h2>
            <div className="bg-slate-50 p-6 rounded-lg mt-8 mb-8">
              <h3 className="font-semibold mb-4">Reach out to us any time — we're always happy to clarify anything.</h3>
              <div className="space-y-4">
                <p>Stinger Builds LLC<br />2600 W Germann Rd<br />Chandler, AZ 85286</p>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-400 mr-2" />
                  <a href="mailto:contact@stingerbuilds.com" className="text-yellow-600 hover:text-yellow-700">
                    contact@stingerbuilds.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-400 mr-2" />
                  <a href="tel:602-900-1093" className="text-yellow-600 hover:text-yellow-700">
                    602-900-1093
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;